import { useFreshSetting } from "@/shared/hooks/useFreshSetting";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	UpsertPurchaseOrderDetailParams,
	UpsertPurchaseOrderMasterParams,
} from "../models/PurchaseOrders";
import {
	getCostCenters,
	getOrderStatus,
	getPurchaseOrderDetails,
	getPurchaseOrders,
	getRollBlanks,
	getSuppliers,
	removePurchaseOrder,
	removePurchaseOrderDetail,
	upsertPurchaseOrderDetail,
	upsertPurchaseOrderMaster,
} from "../repositories/PurchaseOrdersRepository";

// True when the entity is in RollShop Import mode (screen read-only).
export const useIsPurchaseOrdersImportMode = (): boolean =>
	useFreshSetting("Rollshop.ImportPurchaseOrders") === "1";

export const usePurchaseOrders = (
	search: string | null,
	includeDeleted = false,
) =>
	useQuery({
		queryKey: ["purchaseOrders", "list", search, includeDeleted],
		queryFn: ({ signal }) =>
			getPurchaseOrders({ search, includeDeleted, signal }),
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const usePurchaseOrderDetails = (purchaseOrderId: number | null) =>
	useQuery({
		queryKey: ["purchaseOrders", "details", purchaseOrderId],
		queryFn: ({ signal }) =>
			getPurchaseOrderDetails({
				purchaseOrderId: purchaseOrderId as number,
				signal,
			}),
		enabled: !!purchaseOrderId,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const useSuppliers = () =>
	useQuery({
		queryKey: ["purchaseOrders", "suppliers"],
		queryFn: ({ signal }) => getSuppliers({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useCostCenters = () =>
	useQuery({
		queryKey: ["purchaseOrders", "costCenters"],
		queryFn: ({ signal }) => getCostCenters({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useOrderStatus = () =>
	useQuery({
		queryKey: ["purchaseOrders", "orderStatus"],
		queryFn: ({ signal }) => getOrderStatus({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useRollBlanks = () =>
	useQuery({
		queryKey: ["purchaseOrders", "rollBlanks"],
		queryFn: ({ signal }) => getRollBlanks({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useUpsertPurchaseOrder = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (params: UpsertPurchaseOrderMasterParams) =>
			upsertPurchaseOrderMaster(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["purchaseOrders", "list"] });
		},
	});
};

export const useRemovePurchaseOrder = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => removePurchaseOrder(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["purchaseOrders", "list"] });
		},
	});
};

export const useUpsertPurchaseOrderDetail = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (params: UpsertPurchaseOrderDetailParams) =>
			upsertPurchaseOrderDetail(params),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["purchaseOrders", "details"],
			});
			queryClient.invalidateQueries({ queryKey: ["purchaseOrders", "list"] });
		},
	});
};

export const useRemovePurchaseOrderDetail = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => removePurchaseOrderDetail(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["purchaseOrders", "details"],
			});
			queryClient.invalidateQueries({ queryKey: ["purchaseOrders", "list"] });
		},
	});
};
